import React, {
  useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo,
} from 'react';
import { Link } from 'react-router-dom';
import {
  Save, Edit3, X, ZoomIn, ZoomOut, RotateCw, Trash2, Tag, Info, Search,
  Camera,
} from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import siteMapService from '../services/siteMapService';
import apiClient from '../utils/apiClient';
import { formatDateTime } from '../utils/datetime';
import { UNIT_STATUS_TILE, UNIT_STATUS_LEGEND, tileSwatch } from '../utils/unitStatusColors';

/**
 * Site Map — free-form, to-scale drag-and-drop canvas (TDD v3.0 §12.5, Phase 3).
 *
 * Units are real-size boxes (a 10x20 is visibly deeper than a 10x10) that you
 * drag from the tray onto the sheet and reposition by dragging. Coordinates
 * are kept in FEET; `scale` is px-per-foot (zoom). Non-unit annotations
 * (office, drive aisle, building, trees, labels) give the sheet real-world
 * context. Status colors are joined live on load, so the map always reflects
 * current occupancy. Mirrors the workflow canvas pattern: absolute-positioned
 * nodes on a relative canvas, plain pointer events, no external deps.
 */

const STATUS_COLORS = UNIT_STATUS_TILE;

// Annotation styling per kind. Cosmetic only — never tied to a unit.
const ANNOTATION_KINDS = {
  office: { label: 'Office', cls: 'bg-amber-50 border-amber-300 text-amber-800' },
  building: { label: 'Building', cls: 'bg-slate-50 border-slate-300 text-slate-500' },
  drive: { label: 'Drive aisle', cls: 'bg-slate-100/70 border-dashed border-slate-300 text-slate-400' },
  road: { label: 'Road', cls: 'bg-slate-100 border-slate-300 text-slate-500' },
  trees: { label: 'Trees', cls: 'bg-green-50 border-green-200 text-green-700' },
  label: { label: 'Label', cls: 'bg-transparent border-transparent text-slate-600' },
};

const DEFAULT_CANVAS = { width_ft: 400, height_ft: 300, scale: 7, snap_ft: 5 };
const MIN_SCALE = 3;      // floor for MANUAL zoom (below this, labels die)
const MAX_SCALE = 24;
// Auto-fit floor raised 0.5 → 3 (owner, 2026-08-11): on a phone the fit
// computed ~1px/ft and the map rendered unreadably small. View mode now
// never drops below 3px/ft — a site too wide to fit pans instead (the
// pinch/pan handlers already cover it; the sheet centers via margin:auto
// so an overflowing fit stays scrollable edge-to-edge).
// Original rationale for the deep floor, kept for history:
// Auto-fit may go further out than an operator can zoom by hand: the whole
// site has to be visible on open no matter how large the lot is. Clamping
// the fit at MIN_SCALE meant a site wider than roughly (panel px / 3) ft
// silently went back to needing scrollbars.
const FIT_MIN_SCALE = 3;

const newId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `a_${Math.floor(performance.now() * 1000)}`;

const SiteMap = () => {
  const { facilityId, currentFacility, loading: facilityLoading } = useFacility();
  // Measured size of the panel the sheet lives in — drives fit-to-content so
  // the map fills the space it has instead of forcing scrollbars.
  const viewportRef = useRef(null);
  const [viewport, setViewport] = useState({ w: 0, h: 0 });
  const [loading, setLoading] = useState(true);
  // Re-attach when loading flips: on FIRST render only the spinner exists,
  // so a once-only effect observed null and the viewport stayed 0x0 —
  // fit-to-content never engaged and the sheet rendered at the saved zoom,
  // clipped by overflow-hidden, immovable (the phone walk-around bug).
  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const r = entry.contentRect;
      setViewport({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, facilityLoading]);
  const [canvas, setCanvas] = useState(DEFAULT_CANVAS);
  const [placements, setPlacements] = useState([]);
  const [unplaced, setUnplaced] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [editMode, setEditMode] = useState(false);
  // Set once the operator touches +/- in view mode: auto-fit steps aside so
  // their zoom sticks. Reset on load and on leaving edit mode.
  const [userZoomed, setUserZoomed] = useState(false);
  const [selected, setSelected] = useState(null); // {type:'unit'|'annotation', id}
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [trayQuery, setTrayQuery] = useState('');
  const [ghost, setGhost] = useState(null); // {unit, cx, cy} while dragging from tray

  const canvasRef = useRef(null);
  const drag = useRef(null);

  // ---- touch gestures: pinch zooms the sheet, one-finger drag pans ----
  //
  // The map is a walk-around tool — on a phone it must behave like a map
  // app. Native listeners with {passive:false}: React registers touch
  // events as passive at the root, so preventDefault in synthetic
  // handlers is ignored and the page scrolls/zooms instead of the sheet.
  // touchAction:'none' on the viewport hands every gesture to us; a plain
  // tap still synthesizes mousedown, so tap-to-select keeps working.
  const gesture = useRef({ effScale: DEFAULT_CANVAS.scale });
  const pinch = useRef(null); // {prevScale} while a pinch is live
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    let start = null;
    const dist = (t) => Math.hypot(
      t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onStart = (e) => {
      if (e.touches.length === 2) {
        const s = gesture.current.effScale;
        start = { mode: 'pinch', dist: dist(e.touches), scale: s };
        pinch.current = { prevScale: s };
        e.preventDefault(); // keep the browser's page-zoom out of it
      } else if (e.touches.length === 1) {
        start = {
          mode: 'pan',
          x: e.touches[0].clientX, y: e.touches[0].clientY,
          sl: el.scrollLeft, st: el.scrollTop, moved: false,
        };
        // no preventDefault: a motionless tap must still become a click
      }
    };
    const onMove = (e) => {
      if (!start) return;
      if (start.mode === 'pinch' && e.touches.length === 2) {
        e.preventDefault();
        const next = Math.max(FIT_MIN_SCALE, Math.min(
          MAX_SCALE, start.scale * (dist(e.touches) / start.dist)));
        setUserZoomed(true);
        setCanvas((c) => ({ ...c, scale: next }));
      } else if (start.mode === 'pan') {
        const dx = e.touches[0].clientX - start.x;
        const dy = e.touches[0].clientY - start.y;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) start.moved = true;
        if (start.moved) {
          e.preventDefault();
          el.scrollLeft = start.sl - dx;
          el.scrollTop = start.st - dy;
        }
      }
    };
    const onEnd = () => {
      start = null;
      pinch.current = null;
    };
    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [loading, facilityLoading]);

  // Keep the sheet under the fingers while pinch-zooming: scale the scroll
  // offsets around the viewport center after each zoom step, else the map
  // flies toward the top-left as it grows.
  useLayoutEffect(() => {
    const el = viewportRef.current;
    const p = pinch.current;
    if (!el || !p) return;
    const ratio = (canvas.scale || 1) / (p.prevScale || 1);
    if (ratio && ratio !== 1) {
      const cx = el.clientWidth / 2;
      const cy = el.clientHeight / 2;
      el.scrollLeft = (el.scrollLeft + cx) * ratio - cx;
      el.scrollTop = (el.scrollTop + cy) * ratio - cy;
      p.prevScale = canvas.scale;
    }
  }, [canvas.scale]);

  const scale = canvas.scale || DEFAULT_CANVAS.scale;
  const snap = canvas.snap_ft || 5;
  const snapTo = useCallback((v) => Math.round(v / snap) * snap, [snap]);

  // Effective footprint after rotation (90° swaps width/height).
  const footprint = (item) =>
    item.rotation === 90 ? { w: item.h, h: item.w } : { w: item.w, h: item.h };

  const bounds = useMemo(() => {
    const boxes = [
      ...placements.map((p) => ({ ...footprint(p), x: p.x, y: p.y })),
      ...annotations.map((a) => ({ x: a.x, y: a.y, w: a.w, h: a.h })),
    ];
    if (!boxes.length) return null;
    const pad = 4; // ft of breathing room so borders aren't flush to the edge
    const minX = Math.max(0, Math.min(...boxes.map((b) => b.x)) - pad);
    const minY = Math.max(0, Math.min(...boxes.map((b) => b.y)) - pad);
    const maxX = Math.max(...boxes.map((b) => b.x + b.w)) + pad;
    const maxY = Math.max(...boxes.map((b) => b.y + b.h)) + pad;
    return { minX, minY, w: maxX - minX, h: maxY - minY };
  }, [placements, annotations]);

  const loadMap = useCallback(async () => {
    if (!facilityId) {
      if (!facilityLoading) setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await siteMapService.get(facilityId);
      setCanvas({ ...DEFAULT_CANVAS, ...(res.data.canvas || {}) });
      setPlacements(res.data.placements || []);
      setUnplaced(res.data.unplaced || []);
      setAnnotations(res.data.annotations || []);
      setDirty(false);
      setSelected(null);
      setUserZoomed(false);
    } catch (err) {
      console.error('Failed to load site map:', err);
      setError('Unable to load site map.');
    } finally {
      setLoading(false);
    }
  }, [facilityId, facilityLoading]);

  useEffect(() => {
    loadMap();
  }, [loadMap]);

  // ---- pointer-driven drag (move placed items, resize annotations, drop new) ----
  const clamp = (v, max) => Math.max(0, Math.min(v, Math.max(0, max)));

  const onCanvasUnitDown = (e, p) => {
    if (!editMode) {
      setSelected({ type: 'unit', id: p.unit_id });
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    drag.current = {
      mode: 'move-unit', id: p.unit_id,
      startX: e.clientX, startY: e.clientY, ox: p.x, oy: p.y, moved: false,
    };
  };

  const onAnnDown = (e, a) => {
    if (!editMode) return;
    e.stopPropagation();
    e.preventDefault();
    drag.current = {
      mode: 'move-ann', id: a.id,
      startX: e.clientX, startY: e.clientY, ox: a.x, oy: a.y, moved: false,
    };
  };

  const onAnnResizeDown = (e, a) => {
    e.stopPropagation();
    e.preventDefault();
    drag.current = {
      mode: 'resize-ann', id: a.id,
      startX: e.clientX, startY: e.clientY, ow: a.w, oh: a.h, moved: true,
    };
  };

  const onTrayDown = (e, u) => {
    if (!editMode) return;
    e.preventDefault();
    drag.current = { mode: 'new', unit: u };
    setGhost({ unit: u, cx: e.clientX, cy: e.clientY });
  };

  const onMouseMove = (e) => {
    const d = drag.current;
    if (!d) return;
    if (d.mode === 'new') {
      setGhost((g) => (g ? { ...g, cx: e.clientX, cy: e.clientY } : g));
      return;
    }
    const dxFt = (e.clientX - d.startX) / scale;
    const dyFt = (e.clientY - d.startY) / scale;
    if (Math.abs(e.clientX - d.startX) > 3 || Math.abs(e.clientY - d.startY) > 3) d.moved = true;

    if (d.mode === 'move-unit') {
      setPlacements((prev) => prev.map((p) => {
        if (p.unit_id !== d.id) return p;
        const fp = footprint(p);
        return {
          ...p,
          x: clamp(snapTo(d.ox + dxFt), canvas.width_ft - fp.w),
          y: clamp(snapTo(d.oy + dyFt), canvas.height_ft - fp.h),
        };
      }));
      setDirty(true);
    } else if (d.mode === 'move-ann') {
      setAnnotations((prev) => prev.map((a) => {
        if (a.id !== d.id) return a;
        return {
          ...a,
          x: clamp(snapTo(d.ox + dxFt), canvas.width_ft - a.w),
          y: clamp(snapTo(d.oy + dyFt), canvas.height_ft - a.h),
        };
      }));
      setDirty(true);
    } else if (d.mode === 'resize-ann') {
      setAnnotations((prev) => prev.map((a) => {
        if (a.id !== d.id) return a;
        return {
          ...a,
          w: Math.max(snap, snapTo(d.ow + dxFt)),
          h: Math.max(snap, snapTo(d.oh + dyFt)),
        };
      }));
      setDirty(true);
    }
  };

  const onMouseUp = (e) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;

    if (d.mode === 'move-unit' && !d.moved) {
      setSelected({ type: 'unit', id: d.id });
    } else if (d.mode === 'move-ann' && !d.moved) {
      setSelected({ type: 'annotation', id: d.id });
    } else if (d.mode === 'new') {
      const rect = canvasRef.current?.getBoundingClientRect();
      setGhost(null);
      if (rect && e.clientX >= rect.left && e.clientX <= rect.right
        && e.clientY >= rect.top && e.clientY <= rect.bottom) {
        const u = d.unit;
        const xFt = clamp(snapTo((e.clientX - rect.left) / scale - u.w / 2), canvas.width_ft - u.w);
        const yFt = clamp(snapTo((e.clientY - rect.top) / scale - u.h / 2), canvas.height_ft - u.h);
        setPlacements((prev) => [
          ...prev,
          { ...u, x: xFt, y: yFt, w: u.w, h: u.h, rotation: 0 },
        ]);
        setUnplaced((prev) => prev.filter((x) => x.unit_id !== u.unit_id));
        setSelected({ type: 'unit', id: u.unit_id });
        setDirty(true);
      }
    }
  };

  // ---- toolbar actions ----
  const zoom = (delta) => {
    const from = fitMode ? Math.max(MIN_SCALE, effScale) : (canvas.scale || 7);
    setUserZoomed(true);
    setCanvas((c) => ({
      ...c, scale: Math.max(MIN_SCALE, Math.min(MAX_SCALE, from + delta)),
    }));
  };

  const rotateSelected = () => {
    if (selected?.type !== 'unit') return;
    setPlacements((prev) => prev.map((p) =>
      p.unit_id === selected.id ? { ...p, rotation: p.rotation === 90 ? 0 : 90 } : p));
    setDirty(true);
  };

  const removeSelected = () => {
    if (!selected) return;
    if (selected.type === 'unit') {
      const removed = placements.find((p) => p.unit_id === selected.id);
      if (removed) {
        setPlacements((prev) => prev.filter((p) => p.unit_id !== selected.id));
        setUnplaced((prev) => [...prev, removed].sort(
          (a, b) => (a.unit_number || '').localeCompare(b.unit_number || '', undefined, { numeric: true })));
      }
    } else {
      setAnnotations((prev) => prev.filter((a) => a.id !== selected.id));
    }
    setSelected(null);
    setDirty(true);
  };

  const addAnnotation = (kind) => {
    const a = {
      id: newId(), kind, label: ANNOTATION_KINDS[kind]?.label || 'Label',
      x: snapTo(canvas.width_ft / 2 - 15), y: snapTo(canvas.height_ft / 2 - 7.5),
      w: kind === 'label' ? 30 : 25, h: kind === 'label' ? 8 : 20,
    };
    setAnnotations((prev) => [...prev, a]);
    setSelected({ type: 'annotation', id: a.id });
    setDirty(true);
  };

  const updateSelectedAnnotation = (patch) => {
    setAnnotations((prev) => prev.map((a) => (a.id === selected?.id ? { ...a, ...patch } : a)));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        canvas,
        placements: placements.map((p) => ({
          unit_id: p.unit_id, x: p.x, y: p.y, w: p.w, h: p.h, rotation: p.rotation || 0,
        })),
        annotations: annotations.map((a) => ({
          id: a.id, kind: a.kind, label: a.label, x: a.x, y: a.y, w: a.w, h: a.h,
        })),
      };
      await siteMapService.save(facilityId, payload);
      setDirty(false);
      setEditMode(false);
      setSelected(null);
    } catch (err) {
      console.error('Failed to save site map:', err);
      setError(err.response?.data?.detail || 'Unable to save site map.');
    } finally {
      setSaving(false);
    }
  };

  const filteredTray = useMemo(() => {
    const q = trayQuery.trim().toLowerCase();
    if (!q) return unplaced;
    return unplaced.filter((u) => (u.unit_number || '').toLowerCase().includes(q));
  }, [unplaced, trayQuery]);

  const selectedUnit = selected?.type === 'unit'
    ? placements.find((p) => p.unit_id === selected.id) : null;
  const selectedAnn = selected?.type === 'annotation'
    ? annotations.find((a) => a.id === selected.id) : null;

  if (loading || facilityLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!facilityId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h2 className="text-lg font-semibold text-slate-900">No facility selected</h2>
        <p className="text-[13px] text-slate-500 mt-1">Create a facility in Settings to build a site map.</p>
      </div>
    );
  }

  // FIT THE SHEET TO WHAT IS ACTUALLY ON IT.
  //
  // The canvas is a fixed 400x300 ft lot. A yard using a corner of it still
  // rendered all 2800x2100 px inside a 72vh scroll box, so viewing the map
  // meant dragging two scrollbars around mostly empty grid.
  //
  // In VIEW mode the sheet is cropped to the content's bounding box and
  // scaled to fit the panel — everything visible at once, no scrolling. EDIT
  // mode keeps the full lot at the operator's chosen zoom, because placing a
  // unit somewhere new requires the empty space to exist and drag math
  // converts pixels back to feet with the raw `scale`.

  const fitMode = !editMode && !userZoomed && !!bounds && viewport.w > 0;
  const fitScale = fitMode
    ? Math.max(FIT_MIN_SCALE, Math.min(
        MAX_SCALE, viewport.w / bounds.w, viewport.h / bounds.h))
    : scale;
  const effScale = fitMode ? fitScale : scale;
  // Latest applied scale for the touch handlers (attached once; they read
  // through this ref instead of stale closures).
  gesture.current.effScale = effScale;
  const originX = fitMode ? bounds.minX : 0;
  const originY = fitMode ? bounds.minY : 0;

  // Feet -> px against the active scale/origin. Every position on the sheet
  // goes through these so view and edit can never drift apart.
  const sx = (ft) => (ft - originX) * effScale;
  const sy = (ft) => (ft - originY) * effScale;
  const sz = (ft) => ft * effScale;

  const innerW = fitMode ? bounds.w * effScale : canvas.width_ft * scale;
  const innerH = fitMode ? bounds.h * effScale : canvas.height_ft * scale;
  const cell = snap * effScale;

  // The panel is worth its width only when it has content: the tray while
  // editing, a selected unit's details, or the "units not placed yet" nudge.
  // Otherwise it held one line of instructions that the toolbar already
  // makes obvious, at the cost of 260px off the map.
  const showSidebar = editMode || !!selected
    || (placements.length === 0 && unplaced.length > 0);

  return (
    <div onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
      {/* Toolbar */}
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Site Map</h1>
          <p className="text-[13px] text-slate-400 mt-0.5">
            {currentFacility?.name || 'Visual facility layout'}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden">
            <button onClick={() => zoom(-1)} className="p-1.5 text-slate-500 hover:bg-slate-50" title="Zoom out">
              <ZoomOut className="h-4 w-4" />
            </button>
            <span className="px-2 text-[11px] text-slate-500 font-mono tabular-nums">{Math.round(effScale)}px/ft</span>
            <button onClick={() => zoom(1)} className="p-1.5 text-slate-500 hover:bg-slate-50" title="Zoom in">
              <ZoomIn className="h-4 w-4" />
            </button>
          </div>
          {editMode && selectedUnit && (
            <button onClick={rotateSelected}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-600 text-[12px] font-medium text-white hover:bg-blue-700">
              <RotateCw className="h-3.5 w-3.5" /> Rotate
            </button>
          )}
          {editMode && selected && (
            <button onClick={removeSelected}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-200 text-[12px] font-medium text-red-600 hover:bg-red-50">
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          )}
          {dirty && editMode && (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[12px] font-medium hover:bg-blue-700 disabled:opacity-50">
              <Save className="h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save layout'}
            </button>
          )}
          <button
            onClick={() => {
              if (editMode && dirty && !window.confirm('Discard unsaved changes?')) return;
              if (editMode) loadMap();
              if (!editMode && fitMode) {
                // Carry the fitted zoom into edit mode, else the sheet jumps
                // to whatever scale was last saved.
                setCanvas((c) => ({ ...c, scale: effScale }));
              } else if (editMode) {
                setUserZoomed(false);   // back to view -> refit
              }
              setEditMode(!editMode);
              setSelected(null);
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium ${
              editMode ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {editMode ? <X className="h-3.5 w-3.5" /> : <Edit3 className="h-3.5 w-3.5" />}
            {editMode ? 'Done' : 'Edit layout'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-[13px] text-red-600">{error}</div>
      )}

      {/* Legend + edit hint */}
      <div className="mb-3 flex items-center gap-4 text-[11px] flex-wrap">
        {UNIT_STATUS_LEGEND.map((l) => (
          <Legend key={l.status} color={tileSwatch(l.status)} label={l.label} />
        ))}
        {editMode && (
          <span className="flex items-center gap-1 text-slate-500">
            <Info className="h-3 w-3" /> Drag a unit from the tray onto the sheet · drag to move · click to select
          </span>
        )}
      </div>

      <div className={`grid grid-cols-1 gap-4 ${
        showSidebar ? 'lg:grid-cols-[1fr_260px]' : ''}`}>
        {/* Canvas */}
        <div
          ref={viewportRef}
          className={`rounded-xl border border-slate-200/80 bg-white p-3 overflow-auto ${
            !editMode && fitMode ? 'flex' : ''}`}
          style={{
            height: '72vh',
            WebkitOverflowScrolling: 'touch',
            // Hand EVERY touch gesture to the pinch/pan handlers above —
            // mixing native scroll with manual panning double-moves the
            // sheet, and native page pinch-zoom fights the map's own zoom.
            touchAction: 'none',
          }}
        >
          <div
            ref={canvasRef}
            // m-auto centers a small sheet AND keeps an overflowing one
            // scrollable to every edge (flex justify-center would clip
            // the top-left past the panel).
            className={`relative ${fitMode ? 'm-auto' : ''}`}
            style={{
              width: innerW, height: innerH, minWidth: innerW,
              backgroundColor: '#fcfcfd',
              backgroundImage:
                'linear-gradient(to right, #eef2f7 1px, transparent 1px), linear-gradient(to bottom, #eef2f7 1px, transparent 1px)',
              backgroundSize: `${cell}px ${cell}px`,
            }}
            onMouseDown={() => { if (editMode) setSelected(null); }}
          >
            {/* annotations (under units) */}
            {annotations.map((a) => {
              const k = ANNOTATION_KINDS[a.kind] || ANNOTATION_KINDS.label;
              const sel = selected?.type === 'annotation' && selected.id === a.id;
              return (
                <div
                  key={a.id}
                  onMouseDown={(e) => onAnnDown(e, a)}
                  className={`absolute border-2 rounded flex items-center justify-center text-center px-1 overflow-hidden ${k.cls} ${
                    editMode ? 'cursor-move' : ''} ${sel ? 'ring-2 ring-blue-400' : ''}`}
                  style={{ left: sx(a.x), top: sy(a.y), width: sz(a.w), height: sz(a.h) }}
                  title={a.label}
                >
                  <span className="text-[10px] font-semibold uppercase tracking-wide leading-tight truncate">
                    {a.label}
                  </span>
                  {editMode && sel && (
                    <span
                      onMouseDown={(e) => onAnnResizeDown(e, a)}
                      className="absolute -right-1 -bottom-1 w-3 h-3 bg-blue-500 rounded-sm cursor-nwse-resize"
                    />
                  )}
                </div>
              );
            })}

            {/* units */}
            {placements.map((p) => {
              const fp = footprint(p);
              const color = STATUS_COLORS[p.status] || STATUS_COLORS.Available;
              const sel = selected?.type === 'unit' && selected.id === p.unit_id;
              const w = sz(fp.w);
              const h = sz(fp.h);
              const showType = h > 26 && w > 30;
              return (
                <div
                  key={p.unit_id}
                  onMouseDown={(e) => onCanvasUnitDown(e, p)}
                  className={`absolute border rounded flex flex-col items-center justify-center leading-tight select-none overflow-hidden ${color} ${
                    editMode ? 'cursor-move' : 'cursor-pointer'} ${sel ? 'ring-2 ring-blue-500 z-10' : ''}`}
                  style={{ left: sx(p.x), top: sy(p.y), width: w, height: h }}
                  title={`${p.unit_number} · ${p.unit_type || ''} · ${p.status}`}
                >
                  <span className="font-bold text-[11px] truncate w-full text-center px-0.5">{p.unit_number}</span>
                  {showType && <span className="opacity-60 text-[9px]">{p.unit_type}</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Side panel: tray (edit) or details (view). Hidden entirely when it
            has nothing to say, so the sheet gets the full width. In view mode
            it is desktop-only — on a phone the details render as the bottom
            sheet below instead (the sidebar would land under a 72vh canvas,
            out of sight of someone standing at the unit). */}
        {showSidebar && (
        <div className={`rounded-xl border border-slate-200/80 bg-white p-4 self-start ${
          editMode ? '' : 'hidden lg:block'}`}>
          {editMode ? (
            <>
              {selectedAnn ? (
                <div className="mb-4">
                  <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Annotation</h3>
                  <input
                    value={selectedAnn.label}
                    onChange={(e) => updateSelectedAnnotation({ label: e.target.value })}
                    className="w-full mb-2 px-2.5 py-1.5 rounded-md border border-slate-200 text-[12px]"
                    placeholder="Label"
                  />
                  <select
                    value={selectedAnn.kind}
                    onChange={(e) => updateSelectedAnnotation({ kind: e.target.value })}
                    className="w-full px-2.5 py-1.5 rounded-md border border-slate-200 text-[12px] bg-white"
                  >
                    {Object.entries(ANNOTATION_KINDS).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Add element</h3>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {Object.entries(ANNOTATION_KINDS).map(([k, v]) => (
                      <button key={k} onClick={() => addAnnotation(k)}
                        className="flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-[11px] text-slate-600 hover:bg-slate-50">
                        <Tag className="h-3 w-3" /> {v.label}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Unit tray ({unplaced.length})
              </h3>
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input
                  value={trayQuery}
                  onChange={(e) => setTrayQuery(e.target.value)}
                  placeholder="Find unit…"
                  className="w-full pl-7 pr-2 py-1.5 rounded-md border border-slate-200 text-[12px]"
                />
              </div>
              <div className="space-y-1 max-h-[44vh] overflow-y-auto">
                {filteredTray.map((u) => (
                  <div
                    key={u.unit_id}
                    onMouseDown={(e) => onTrayDown(e, u)}
                    className="flex items-center justify-between px-2.5 py-1.5 rounded-md border border-slate-200 text-[12px] cursor-grab active:cursor-grabbing hover:bg-slate-50 select-none"
                  >
                    <span className="font-medium text-slate-700">{u.unit_number}</span>
                    <span className="text-slate-400 text-[11px]">{u.unit_type}</span>
                  </div>
                ))}
                {filteredTray.length === 0 && (
                  <p className="text-[12px] text-slate-400 py-2">
                    {unplaced.length === 0 ? 'All units placed.' : 'No matches.'}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              {selectedUnit ? (
                <UnitDetailsCard
                  unit={selectedUnit}
                  facilityId={facilityId}
                  timeZone={currentFacility?.timezone}
                  onClose={() => setSelected(null)}
                />
              ) : null}
              {placements.length === 0 && unplaced.length > 0 && (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-[12px] text-blue-900">
                  <strong>{unplaced.length} unit{unplaced.length === 1 ? '' : 's'}</strong> not on the map yet.
                  Click <strong>Edit layout</strong>, then drag them from the tray onto the sheet.
                </div>
              )}
            </>
          )}
        </div>
        )}
      </div>

      {/* Phone: details as a bottom sheet over the map, so tapping unit
          after unit on a walk-around keeps the sheet in reach of a thumb. */}
      {!editMode && selectedUnit && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 rounded-t-2xl border-t border-slate-200 bg-white shadow-2xl p-4 max-h-[60vh] overflow-y-auto">
          <UnitDetailsCard
            unit={selectedUnit}
            facilityId={facilityId}
            timeZone={currentFacility?.timezone}
            onClose={() => setSelected(null)}
          />
        </div>
      )}

      {/* drag ghost while placing from the tray */}
      {ghost && (
        <div
          className="fixed pointer-events-none z-50 rounded border border-blue-400 bg-blue-100/80 text-blue-900 text-[11px] font-bold flex items-center justify-center shadow-lg"
          style={{
            left: ghost.cx + 8, top: ghost.cy + 8,
            width: Math.max(28, ghost.unit.w * scale), height: Math.max(20, ghost.unit.h * scale),
          }}
        >
          {ghost.unit.unit_number}
        </div>
      )}
    </div>
  );
};

/**
 * Authenticated thumbnail. /api/uploads/* requires the operator JWT, so a
 * bare <img src> would 401 — same blob-open pattern as the lease PDFs.
 */
const AuthImg = ({ path, className }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    apiClient.get(path, { responseType: 'blob' })
      .then((r) => {
        objectUrl = URL.createObjectURL(r.data);
        if (!cancelled) setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);
  if (!url) {
    return <span className={`${className} bg-slate-100 animate-pulse inline-block`} />;
  }
  return (
    <img
      src={url}
      alt="Note attachment"
      className={`${className} cursor-pointer`}
      onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
    />
  );
};

/**
 * The walk-around card: who's in the unit, the recent field notes, and a
 * composer that writes ONE note to BOTH timelines (unit history +, when
 * occupied, the tenant's notes) with optional phone-camera photos.
 * Timestamps render on the facility clock.
 */
const UnitDetailsCard = ({ unit, facilityId, timeZone, onClose }) => {
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [text, setText] = useState('');
  const [photos, setPhotos] = useState([]);
  const [savingNote, setSavingNote] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);
  const [noteError, setNoteError] = useState(null);
  const fileRef = useRef(null);

  const loadNotes = useCallback(async () => {
    setNotesLoading(true);
    try {
      const res = await siteMapService.unitNotes(facilityId, unit.unit_id);
      setNotes(res.data.notes || []);
    } catch {
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  }, [facilityId, unit.unit_id]);

  useEffect(() => {
    loadNotes();
    // Fresh unit tapped: the composer belongs to the new unit.
    setText('');
    setPhotos([]);
    setSavedMsg(null);
    setNoteError(null);
    if (fileRef.current) fileRef.current.value = '';
  }, [loadNotes]);

  const saveNote = async () => {
    if (!text.trim() || savingNote) return;
    setSavingNote(true);
    setNoteError(null);
    setSavedMsg(null);
    try {
      const res = await siteMapService.addUnitNote(
        facilityId, unit.unit_id, text.trim(), photos,
      );
      setText('');
      setPhotos([]);
      if (fileRef.current) fileRef.current.value = '';
      setSavedMsg(res.data.mirrored_to_customer
        ? `Saved to unit history and ${res.data.rented_to}'s notes.`
        : 'Saved to unit history.');
      loadNotes();
    } catch (err) {
      setNoteError(err.response?.data?.detail || 'Could not save the note.');
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="font-semibold text-slate-900 text-[15px]">Unit {unit.unit_number}</p>
        <button
          onClick={onClose}
          title="Close details and show the full map"
          aria-label="Close details"
          className="p-1.5 -m-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
        >
          <X size={16} />
        </button>
      </div>

      <div className="space-y-2 text-[13px]">
        <div className="flex justify-between gap-2">
          <span className="text-slate-400">Rented to</span>
          {unit.customer_name ? (
            <Link
              to={`/customers/${unit.customer_id}`}
              className="text-blue-600 font-medium hover:underline text-right"
            >
              {unit.customer_name}
            </Link>
          ) : (
            <span className="text-slate-700 font-medium">—</span>
          )}
        </div>
        <Row label="Type" value={unit.unit_type} />
        <Row label="Status" value={unit.status} />
        <Row label="Rate" value={unit.monthly_rate != null ? `$${unit.monthly_rate}/mo` : '—'} />
      </div>

      <div className="mt-4 border-t border-slate-100 pt-3">
        <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Add note</h4>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder={`Note for unit ${unit.unit_number}…`}
          className="w-full px-2.5 py-2 rounded-md border border-slate-200 text-[13px]"
        />
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-600 text-[12px] font-medium text-white hover:bg-blue-700"
          >
            <Camera className="h-3.5 w-3.5" />
            {photos.length ? `${photos.length} photo${photos.length === 1 ? '' : 's'}` : 'Photo'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => setPhotos(Array.from(e.target.files || []))}
          />
          <button
            onClick={saveNote}
            disabled={savingNote || !text.trim()}
            className="ml-auto px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[12px] font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {savingNote ? 'Saving…' : 'Save note'}
          </button>
        </div>
        {savedMsg && <p className="mt-2 text-[12px] text-green-600">{savedMsg}</p>}
        {noteError && <p className="mt-2 text-[12px] text-red-600">{noteError}</p>}
      </div>

      <div className="mt-4 border-t border-slate-100 pt-3">
        <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Recent notes</h4>
        {notesLoading ? (
          <p className="text-[12px] text-slate-400">Loading…</p>
        ) : notes.length === 0 ? (
          <p className="text-[12px] text-slate-400">No notes yet.</p>
        ) : (
          <div className="space-y-2 max-h-[30vh] overflow-y-auto">
            {notes.map((n) => (
              <div key={n.id} className="rounded-md bg-slate-50 px-2.5 py-2">
                <p className="text-[12px] text-slate-700 whitespace-pre-wrap">{n.text}</p>
                {(n.photo_urls || []).length > 0 && (
                  <div className="mt-1.5 flex gap-1.5 flex-wrap">
                    {n.photo_urls.map((u) => (
                      <AuthImg
                        key={u}
                        path={u}
                        className="h-12 w-12 object-cover rounded border border-slate-200"
                      />
                    ))}
                  </div>
                )}
                <p className="mt-1 text-[11px] text-slate-400">
                  {formatDateTime(n.at, timeZone)}
                  {n.actor ? ` · ${n.actor}` : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Legend = ({ color, label }) => (
  <span className="flex items-center gap-1.5 text-slate-500">
    <span className={`w-3 h-3 border rounded-sm ${color}`} /> {label}
  </span>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-slate-400">{label}</span>
    <span className="text-slate-700 font-medium">{value}</span>
  </div>
);

export default SiteMap;
